// VMTable — Step 3 VM list grouped by application.
// Renders all VMs for a project, grouped by application, with add / edit / delete actions.

import { useState } from 'react';
import { useProjectStore } from '../../store/projectStore';
import type { VMConfig, Application, Tier, NetworkSecurityDomain } from '../../types';
import VMForm from './VMForm';

// ─── Security domain colour pills ─────────────────────────────────────────────

const DOMAIN_COLOURS: Record<NetworkSecurityDomain, string> = {
  internal: 'bg-blue-900/40 text-blue-300',
  dmz: 'bg-orange-900/40 text-orange-300',
  external: 'bg-red-900/40 text-red-300',
  management: 'bg-purple-900/40 text-purple-300',
};

const DOMAIN_LABELS: Record<NetworkSecurityDomain, string> = {
  internal: 'Internal',
  dmz: 'DMZ',
  external: 'External',
  management: 'Mgmt',
};

// ─── Component ────────────────────────────────────────────────────────────────

export default function VMTable() {
  const project = useProjectStore(s => s.project);
  const addVM = useProjectStore(s => s.addVM);
  const updateVM = useProjectStore(s => s.updateVM);
  const deleteVM = useProjectStore(s => s.deleteVM);

  const [addingToApp, setAddingToApp] = useState<string | null>(null);
  const [editingVm, setEditingVm] = useState<VMConfig | null>(null);

  if (!project) {
    return <p className="text-sm text-[#8FA4B5]">No project loaded.</p>;
  }

  const allVmCount = project.applications.reduce((n, a) => n + a.vms.length, 0);

  return (
    <div className="space-y-6">
      {/* Summary row */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-[#8FA4B5]">
          {allVmCount} VM{allVmCount !== 1 ? 's' : ''} across{' '}
          {project.applications.length} application{project.applications.length !== 1 ? 's' : ''}
        </p>
      </div>

      {project.applications.length === 0 && (
        <p className="text-sm text-[#4C5C68] italic">
          No applications defined yet. Add applications in Step 2.
        </p>
      )}

      {project.applications.map(app => (
        <ApplicationGroup
          key={app.id}
          app={app}
          onAdd={() => setAddingToApp(app.id)}
          onEdit={setEditingVm}
          onDelete={vm => {
            if (window.confirm(`Delete VM "${vm.name}"?`)) deleteVM(vm.id);
          }}
        />
      ))}

      {/* Add VM modal */}
      {addingToApp !== null && (() => {
        const app = project.applications.find(a => a.id === addingToApp);
        const tiers = app?.tiers ?? [];
        const defaultTierId = tiers[0]?.id ?? '';
        return (
          <VMForm
            applicationId={addingToApp}
            tierId={defaultTierId}
            tiers={tiers}
            onSave={(vm, tierId) => {
              addVM(addingToApp, tierId, vm);
              setAddingToApp(null);
            }}
            onCancel={() => setAddingToApp(null)}
          />
        );
      })()}

      {/* Edit VM modal */}
      {editingVm !== null && (() => {
        const app = project.applications.find(a => a.id === editingVm.applicationId);
        const tiers = app?.tiers ?? [];
        return (
          <VMForm
            applicationId={editingVm.applicationId}
            tierId={editingVm.tierId}
            tiers={tiers}
            initialVm={editingVm}
            onSave={(partial, tierId) => {
              updateVM(editingVm.id, { ...partial, tierId });
              setEditingVm(null);
            }}
            onCancel={() => setEditingVm(null)}
          />
        );
      })()}
    </div>
  );
}

// ─── ApplicationGroup ────────────────────────────────────────────────────────

interface ApplicationGroupProps {
  app: Application;
  onAdd: () => void;
  onEdit: (vm: VMConfig) => void;
  onDelete: (vm: VMConfig) => void;
}

function ApplicationGroup({ app, onAdd, onEdit, onDelete }: ApplicationGroupProps) {
  return (
    <div className="border border-[rgba(76,92,104,0.35)] rounded-lg overflow-hidden">
      {/* App header */}
      <div data-app-group-header className="bg-[#132840] border-b border-[rgba(76,92,104,0.35)] px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <span className="font-semibold text-[#E8EDF2] font-[Poppins]">{app.name}</span>
          {app.description && (
            <span className="text-sm text-[#8FA4B5]">{app.description}</span>
          )}
          <span className="text-xs bg-[#0a1929] text-[#4C5C68] px-2 py-0.5 rounded">
            {app.vms.length} VM{app.vms.length !== 1 ? 's' : ''}
          </span>
        </div>
        <button
          type="button"
          onClick={onAdd}
          data-add-vm-btn
          className="text-sm text-[#F5A621] font-medium border border-[rgba(245,166,33,0.5)] bg-transparent px-3 py-1 rounded-md hover:bg-[rgba(245,166,33,0.12)]"
        >
          + Add VM
        </button>
      </div>

      {/* VM table */}
      {app.vms.length === 0 ? (
        <div className="px-4 py-6 text-center text-sm text-[#4C5C68] italic bg-[#0d1f30]">
          No VMs yet.{' '}
          <button
            type="button"
            onClick={onAdd}
            className="text-[#CEECF8] hover:underline not-italic"
          >
            Add the first VM
          </button>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[rgba(76,92,104,0.35)] text-left text-xs text-[#4C5C68] uppercase tracking-wide bg-[#0d1f30]">
                <th className="px-4 py-2">Name</th>
                <th className="px-4 py-2 text-right">Qty</th>
                <th className="px-4 py-2 text-right">vCPU</th>
                <th className="px-4 py-2 text-right">RAM (GB)</th>
                <th className="px-4 py-2 text-right">SAN (GB)</th>
                <th className="px-4 py-2 text-right">SAN IOPS</th>
                <th className="px-4 py-2 text-right">BW (Mbps)</th>
                <th className="px-4 py-2">Security</th>
                <th className="px-4 py-2">Latency</th>
                <th className="px-4 py-2">Env</th>
                <th className="px-4 py-2">Site</th>
                <th className="px-4 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {app.tiers.length > 1
                ? app.tiers
                    .slice()
                    .sort((a, b) => a.sortOrder - b.sortOrder)
                    .map(tier => {
                      const tierVms = app.vms.filter(vm => vm.tierId === tier.id);
                      if (tierVms.length === 0) return null;
                      const totalVcpu = tierVms.reduce((s, vm) => s + vm.vcpu * vm.quantity, 0);
                      const totalRam = tierVms.reduce((s, vm) => s + vm.memoryGb * vm.quantity, 0);
                      return (
                        <TierGroup key={tier.id} tier={tier} vmCount={tierVms.length} totalVcpu={totalVcpu} totalRam={totalRam}>
                          {tierVms.map((vm, i) => (
                            <VMRow key={vm.id} vm={vm} striped={i % 2 === 1} onEdit={() => onEdit(vm)} onDelete={() => onDelete(vm)} />
                          ))}
                        </TierGroup>
                      );
                    })
                : app.vms.map((vm, i) => (
                    <VMRow key={vm.id} vm={vm} striped={i % 2 === 1} onEdit={() => onEdit(vm)} onDelete={() => onDelete(vm)} />
                  ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── TierGroup ───────────────────────────────────────────────────────────────

interface TierGroupProps {
  tier: Tier;
  vmCount: number;
  totalVcpu: number;
  totalRam: number;
  children: React.ReactNode;
}

function TierGroup({ tier, vmCount, totalVcpu, totalRam, children }: TierGroupProps) {
  return (
    <>
      <tr className="bg-[#0a1929] border-t border-[rgba(76,92,104,0.25)]">
        <td colSpan={12} className="px-4 py-1.5">
          <span className="text-xs font-semibold text-[#8FA4B5] uppercase tracking-wide">{tier.name}</span>
          <span className="ml-2 text-[10px] text-[#4C5C68]">
            {vmCount} VM{vmCount !== 1 ? 's' : ''} · {totalVcpu} vCPU · {totalRam} GB RAM
          </span>
        </td>
      </tr>
      {children}
    </>
  );
}

// ─── VMRow ────────────────────────────────────────────────────────────────────

interface VMRowProps {
  vm: VMConfig;
  striped: boolean;
  onEdit: () => void;
  onDelete: () => void;
}

function VMRow({ vm, striped, onEdit, onDelete }: VMRowProps) {
  const latencyShort: Record<string, string> = {
    standard: 'Std',
    low_latency: 'Low',
    ultra_low_latency: 'ULL',
  };
  const envShort: Record<string, string> = {
    production: 'Prod',
    pre_production: 'Pre-Prod',
    development: 'Dev',
  };
  const siteShort: Record<string, string> = {
    both: 'Both',
    primary: 'Pri',
    dr: 'DR',
  };

  return (
    <tr
      className={`${striped ? 'bg-[#0a1929]' : 'bg-[#0d1f30]'} cursor-pointer hover:bg-[#132840] transition-colors`}
      onClick={onEdit}
    >
      <td className="px-4 py-2 font-medium text-[#E8EDF2]">
        {vm.name}
        {vm.notes && (
          <span
            className="ml-1 text-[#CEECF8] cursor-help"
            title={vm.notes}
          >
            ℹ
          </span>
        )}
      </td>
      <td className="px-4 py-2 text-right tabular-nums text-[#E8EDF2]">{vm.quantity}</td>
      <td className="px-4 py-2 text-right tabular-nums text-[#E8EDF2]">{vm.vcpu}</td>
      <td className="px-4 py-2 text-right tabular-nums text-[#E8EDF2]">{vm.memoryGb}</td>
      <td className="px-4 py-2 text-right tabular-nums text-[#E8EDF2]">{vm.diskSanGb}</td>
      <td className="px-4 py-2 text-right tabular-nums text-[#E8EDF2]">{vm.diskSanIops.toLocaleString()}</td>
      <td className="px-4 py-2 text-right tabular-nums text-[#E8EDF2]">{vm.networkBandwidthMbps.toLocaleString()}</td>
      <td className="px-4 py-2">
        <span
          className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${
            DOMAIN_COLOURS[vm.networkConfig.securityDomain]
          }`}
        >
          {DOMAIN_LABELS[vm.networkConfig.securityDomain]}
        </span>
      </td>
      <td className="px-4 py-2">
        <span
          className={`text-xs ${
            vm.networkConfig.latencySensitivity === 'ultra_low_latency'
              ? 'text-red-400 font-semibold'
              : 'text-[#8FA4B5]'
          }`}
        >
          {latencyShort[vm.networkConfig.latencySensitivity] ?? vm.networkConfig.latencySensitivity}
        </span>
      </td>
      <td className="px-4 py-2 text-xs text-[#8FA4B5]">{envShort[vm.environment] ?? vm.environment}</td>
      <td className="px-4 py-2 text-xs text-[#8FA4B5]">{siteShort[vm.siteAssignment] ?? vm.siteAssignment}</td>
      <td className="px-4 py-2">
        <div className="flex items-center gap-3 justify-end">
          <button
            type="button"
            onClick={e => { e.stopPropagation(); onDelete(); }}
            className="text-xs text-[#E05555] hover:underline"
          >
            Delete
          </button>
        </div>
      </td>
    </tr>
  );
}
